import { Alert, Box, LinearProgress, Stack } from "@mui/material";

import { currencyFormatter, formatChannelLabel } from "../../../../lib/presentation";
import type { ClientPurchasedItemsResponse } from "../../../../lib/api/types";
import type { MasterProfile, OrderRow } from "../../../../types";
import PurchaseBehaviorChannelPanel from "./PurchaseBehaviorChannelPanel";
import PurchaseBehaviorMetric from "./PurchaseBehaviorMetric";
import PurchaseBehaviorRankedPanel from "./PurchaseBehaviorRankedPanel";
import PurchaseBehaviorTopItemsTable from "./PurchaseBehaviorTopItemsTable";
import { averageOrderIntervalDays, daysSince, parseBehaviorDate, rankBy } from "./purchaseBehaviorUtils";

type Client360PurchaseBehaviorProps = {
    data?: ClientPurchasedItemsResponse;
    isLoading: boolean;
    orders: OrderRow[];
    profile: MasterProfile;
};

export default function Client360PurchaseBehavior({
    data,
    isLoading,
    orders,
    profile,
}: Client360PurchaseBehaviorProps) {
    const purchasedItems = data?.items ?? [];
    const topItems = (data?.topItems ?? []).slice(0, 10);
    const orderCount = orders.length;
    const turnoverFromOrders = orders.reduce((sum, order) => sum + order.value, 0);
    const turnoverFromItems = purchasedItems.reduce((sum, row) => sum + row.totalValue, 0);
    const turnover = turnoverFromOrders > 0 ? turnoverFromOrders : turnoverFromItems;
    const averageBasket = orderCount > 0 ? turnover / orderCount : 0;
    const latestOrder = orders[0] ?? null;
    const missingDays = daysSince(parseBehaviorDate(latestOrder?.createdAt));
    const averageInterval = averageOrderIntervalDays(orders);

    const topCategories = rankBy(
        topItems.length > 0 ? topItems : purchasedItems,
        (row) => row.category || "Без категория",
        (row) => row.totalValue,
    );
    const topStores = rankBy(orders, (order) => order.location, (order) => order.value);
    const topChannels = rankBy(orders, (order) => order.channel, (order) => order.value);
    const preferredStore = topStores[0]?.label ?? profile.preferredStore ?? "—";
    const preferredChannel = topChannels[0]?.label ?? "—";

    return (
        <Stack spacing={2}>
            {isLoading ? <LinearProgress sx={{ borderRadius: 999 }} /> : null}

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                    gap: 1.25,
                }}
            >
                <PurchaseBehaviorMetric label="Оборот за 12 месеца" value={currencyFormatter.format(turnover)} />
                <PurchaseBehaviorMetric label="Брой покупки" value={String(orderCount)} />
                <PurchaseBehaviorMetric label="Средна кошница" value={currencyFormatter.format(averageBasket)} />
                <PurchaseBehaviorMetric label="Последна покупка" value={latestOrder?.createdAt || "—"} />
                <PurchaseBehaviorMetric label="Предпочитан магазин" value={preferredStore} />
                <PurchaseBehaviorMetric label="Предпочитан канал" value={formatChannelLabel(preferredChannel)} />
                <PurchaseBehaviorMetric
                    label="Среден интервал"
                    value={averageInterval === null ? "—" : `${Math.round(averageInterval)} дни`}
                />
                <PurchaseBehaviorMetric
                    label="Липсва от"
                    value={missingDays === null ? "Няма покупка" : `${missingDays} дни`}
                    tone={missingDays !== null && missingDays > 45 ? "warning" : "normal"}
                />
            </Box>

            {missingDays !== null && missingDays > 45 ? (
                <Alert severity="warning">
                    Клиентът не е купувал от {missingDays} дни. Това е добър кандидат за последващ контакт или оферта.
                </Alert>
            ) : null}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.3fr 0.9fr" }, gap: 2 }}>
                <PurchaseBehaviorTopItemsTable rows={topItems} />

                <Stack spacing={2}>
                    <PurchaseBehaviorRankedPanel title="Топ категории" rows={topCategories} />
                    <PurchaseBehaviorRankedPanel title="Къде купува" rows={topStores} />
                    <PurchaseBehaviorChannelPanel rows={topChannels} />
                </Stack>
            </Box>
        </Stack>
    );
}
